import React, { lazy } from 'react';

import type { TaskStatus } from '@app/api/editor';
import { ReactWebComponent } from '@app/utils/web-components';

import type { TaskType } from './types';

const AppProviders = lazy(() => import('@app/components/AppProviders'));
const TranslationEditor = lazy(() => import('.'));

const emitSocketEvent = (event: string, data: Record<string, string | number | boolean | null>) => {
    // @ts-expect-error globalSocket is not defined
    window.globalSocket?.emit(event, data);
};

class ReactEditorComponent extends ReactWebComponent {
    static get observedAttributes() {
        return [
            'projectId',
            'langId',
            'keyId',
            'value',
            'source-value',
            'translationId',
            'autoReview',
            'isBaseLanguage',
            'isReviewingAllowed',
            'isSegmentationEnabled',
            'isPlural',
            'segmentNumber',
            'siblingSegments',
            'activeTask',
            'item-id',
        ];
    }

    render() {
        const projectId = this.getAttribute('projectId');
        const langId = this.jsonProp<number>('langId');
        const value = this.getAttribute('value');
        const sourceValue = this.prop('source-value');
        const translationId = this.jsonProp<number>('translationId');
        const itemId = this.jsonProp<number>('item-id');
        const autoReview = this.jsonProp<boolean>('autoReview');
        const isBaseLanguage = this.jsonProp<boolean>('isBaseLanguage');
        const isReviewingAllowed = this.jsonProp<boolean>('isReviewingAllowed');
        // const isSegmentationEnabled = this.jsonProp<boolean>('isSegmentationEnabled');
        const isPlural = this.jsonProp<boolean>('isPlural');
        const activeTask =
            this.jsonProp<{
                hasActiveTask: string;
                activeTaskId?: string;
                activeTaskItemId?: string;
                activeTaskStatus?: TaskStatus;
                activeTaskType?: TaskType;
                autoCloseItem?: string;
            }>('activeTask') ?? null;
        const segmentNumber = this.jsonProp<number>('segmentNumber');
        const siblingSegments =
            // eslint-disable-next-line @typescript-eslint/naming-convention
            this.jsonProp<{ translation: string; segment_number: string }[]>('siblingSegments') || [];

        const segmentCharacterCount = siblingSegments.reduce(
            (total, segment) =>
                total + (parseInt(segment.segment_number, 10) !== segmentNumber ? segment.translation.length : 0),
            0,
        );

        const handleTaskItemCompleted = (completed: boolean) => {
            if (!activeTask) {
                return;
            }

            const { activeTaskId, activeTaskItemId } = activeTask;
            const taskId = Number(activeTaskId);
            const taskItemId = Number(activeTaskItemId);

            emitSocketEvent('task item complete', {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                task_id: taskId,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                task_item_id: taskItemId,
                complete: completed,
                projectId,
            });

            document.dispatchEvent(
                new CustomEvent('task_item_complete', {
                    detail: {
                        taskId,
                        taskItemId,
                        completed,
                    },
                }),
            );
        };

        if (!projectId || itemId === null || translationId === null || langId === null || activeTask === null) {
            return <>Error</>;
        }

        return (
            <AppProviders>
                <TranslationEditor
                    projectId={projectId}
                    translationId={translationId}
                    value={value}
                    sourceValue={sourceValue}
                    segmentCharacterCount={segmentCharacterCount}
                    languageId={langId}
                    autoReview={autoReview || false}
                    isBaseLanguage={isBaseLanguage || false}
                    isReviewingAllowed={isReviewingAllowed || false}
                    isPlural={isPlural || false}
                    activeTask={{
                        ...activeTask,
                        activeTaskItemId: activeTask.activeTaskItemId
                            ? Number(activeTask?.activeTaskItemId)
                            : undefined,
                        hasActiveTask: activeTask?.hasActiveTask === '1',
                        autoCloseItem: activeTask?.autoCloseItem === '1',
                    }}
                    onSaveTranslation={handleTaskItemCompleted}
                    itemId={itemId}
                />
            </AppProviders>
        );
    }
}

customElements.define('editor-new-root', ReactEditorComponent);
